'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { useForm, useFieldArray } from 'react-hook-form';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Eye, Info, Building2, User, Phone, Mail, Hash, MapPin, FileText, AlertCircle, Loader2, ArrowLeft, ReceiptText } from 'lucide-react';
import Link from 'next/link';
import { Quotation, LineItem, GSTType, QuotationStatus } from '@/lib/types';
import { getQuotationById, saveQuotation, generateQuoteNumber } from '@/lib/store';
import { calcLineItem, calcTotals, formatINR, numberToWords } from '@/lib/calculations';
import FileUploadParser, { ParsedLineItem } from './FileUploadParser';

const UNITS = ['Nos', 'Set', 'Job', 'Roll', 'Box', 'Mtr', 'Kg', 'Ltr', 'Sqft', 'Sqm', 'Pair', 'Lot'];
const GST_RATES = [0, 5, 12, 18, 28];

interface FormValues {
  quoteNumber: string;
  date: string;
  validUntil: string;
  status: QuotationStatus;
  subject: string;
  preparedBy: string;
  clientName: string;
  clientCompany: string;
  clientAddress: string;
  clientPhone: string;
  clientEmail: string;
  clientGSTIN: string;
  gstType: GSTType;
  lineItems: {
    id: string;
    srNo: number;
    description: string;
    hsn: string;
    qty: number | string;
    unit: string;
    rate: number | string;
    gstRate: number;
  }[];
  terms: string;
  notes: string;
}

function todayStr() {
  return new Date().toLocaleDateString('en-GB');
}
function addDays(dateStr: string, days: number) {
  const parts = dateStr.split('/');
  const d = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString('en-GB');
}

export default function QuotationEditorContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const editId = searchParams.get('id');
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);

  const defaultDate = todayStr();
  const defaultValid = addDays(defaultDate, 30);

  const {
    register, control, handleSubmit, watch, setValue, reset,
    formState: { errors, isDirty }
  } = useForm<FormValues>({
    defaultValues: {
      quoteNumber: '',
      date: defaultDate,
      validUntil: defaultValid,
      status: 'Draft',
      subject: '',
      preparedBy: '',
      clientName: '',
      clientCompany: '',
      clientAddress: '',
      clientPhone: '',
      clientEmail: '',
      clientGSTIN: '',
      gstType: 'CGST_SGST',
      lineItems: [
        { id: `li-new-1`, srNo: 1, description: '', hsn: '', qty: 1, unit: 'Nos', rate: 0, gstRate: 18 }
      ],
      terms: 'Payment within 30 days of invoice. Goods once sold will not be taken back. Subject to New Delhi jurisdiction.',
      notes: '',
    }
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'lineItems' });
  const watchItems = watch('lineItems');
  const watchGstType = watch('gstType');

  const handleFileParsed = useCallback((items: ParsedLineItem[]) => {
    const newItems = items.map((item, i) => ({
      id: `li-import-${Date.now()}-${i}`,
      srNo: i + 1,
      description: item.description,
      hsn: item.hsn,
      qty: item.qty,
      unit: item.unit,
      rate: item.rate,
      gstRate: item.gstRate,
    }));
    setValue('lineItems', newItems, { shouldDirty: true });
  }, [setValue]);

  // Load existing quotation
  useEffect(() => {
    if (editId) {
      const q = getQuotationById(editId);
      if (q) {
        reset({
          quoteNumber: q.quoteNumber,
          date: q.date,
          validUntil: q.validUntil,
          status: q.status,
          subject: q.subject,
          preparedBy: q.preparedBy,
          clientName: q.clientName,
          clientCompany: q.clientCompany,
          clientAddress: q.clientAddress,
          clientPhone: q.clientPhone,
          clientEmail: q.clientEmail,
          clientGSTIN: q.clientGSTIN,
          gstType: q.gstType,
          lineItems: q.lineItems.map(li => ({
            id: li.id,
            srNo: li.srNo,
            description: li.description,
            hsn: li.hsn,
            qty: li.qty,
            unit: li.unit,
            rate: li.rate,
            gstRate: li.gstRate,
          })),
          terms: q.terms,
          notes: q.notes,
        });
        setSavedId(editId);
      }
    } else {
      // Generate new quote number
      const qn = generateQuoteNumber();
      setValue('quoteNumber', qn);
    }
  }, [editId, reset, setValue]);

  // Computed line items
  const computedItems: LineItem[] = watchItems.map((item, i) =>
    calcLineItem({
      id: item.id,
      srNo: i + 1,
      description: item.description,
      hsn: item.hsn,
      qty: Number(item.qty) || 0,
      unit: item.unit,
      rate: Number(item.rate) || 0,
      gstRate: item.gstRate,
    })
  );

  const { subtotal, totalGST, grandTotal } = calcTotals(computedItems);

  const onSubmit = async (data: FormValues, statusOverride?: QuotationStatus) => {
    setSaving(true);
    await new Promise(r => setTimeout(r, 400));

    const items: LineItem[] = data.lineItems.map((item, i) =>
      calcLineItem({
        id: item.id,
        srNo: i + 1,
        description: item.description,
        hsn: item.hsn,
        qty: Number(item.qty) || 0,
        unit: item.unit,
        rate: Number(item.rate) || 0,
        gstRate: item.gstRate,
      })
    );
    const totals = calcTotals(items);

    const q: Quotation = {
      id: savedId || `qt-${Date.now()}`,
      quoteNumber: data.quoteNumber,
      date: data.date,
      validUntil: data.validUntil,
      status: statusOverride || data.status,
      clientName: data.clientName,
      clientCompany: data.clientCompany,
      clientAddress: data.clientAddress,
      clientPhone: data.clientPhone,
      clientEmail: data.clientEmail,
      clientGSTIN: data.clientGSTIN,
      subject: data.subject,
      gstType: data.gstType,
      lineItems: items,
      subtotal: totals.subtotal,
      totalGST: totals.totalGST,
      grandTotal: totals.grandTotal,
      terms: data.terms,
      notes: data.notes,
      preparedBy: data.preparedBy,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Backend integration point: POST/PUT /api/quotations
    saveQuotation(q);
    setSavedId(q.id);
    setSaving(false);
    toast.success(editId ? 'Quotation updated successfully' : 'Quotation saved successfully');
    if (!editId) {
      router.push(`/quotation-editor?id=${q.id}`);
    }
  };

  const handleSaveAndPreview = handleSubmit(async (data) => {
    await onSubmit(data);
    if (savedId) router.push(`/quotation-preview?id=${savedId}`);
    else setTimeout(() => router.push(`/quotation-preview?id=${savedId}`), 500);
  });

  return (
    <div className="max-w-screen-2xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link href="/quotations-management" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
          <ArrowLeft size={18} />
        </Link>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-slate-900">{editId ? 'Edit Quotation' : 'New Quotation'}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{watch('quoteNumber') || 'Generating quote number...'}</p>
        </div>
        <div className="flex items-center gap-2">
          {savedId && (
            <Link
              href={`/quotation-preview?id=${savedId}`}
              className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <Eye size={15} />
              Preview
            </Link>
          )}
          <button
            onClick={handleSubmit(d => onSubmit(d))}
            disabled={saving}
            className="flex items-center gap-2 bg-[#1B2A4A] hover:bg-[#243556] text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 active:scale-95 disabled:opacity-70 min-w-[110px] justify-center"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit(d => onSubmit(d))} className="space-y-6">
        {/* Quote Meta + Client Info */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Quote Details */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
            <h2 className="text-sm font-700 text-slate-800 flex items-center gap-2 uppercase tracking-wider">
              <ReceiptText size={15} className="text-[#E8620A]" />
              Quote Details
            </h2>

            <div>
              <label className="block text-xs font-600 text-slate-600 mb-1">Quote Number <span className="text-red-500">*</span></label>
              <input
                {...register('quoteNumber', { required: 'Quote number required' })}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#E8620A]/30 focus:border-[#E8620A]"
                placeholder="PT/2026/0001"
              />
              {errors.quoteNumber && <p className="text-red-500 text-xs mt-1">{errors.quoteNumber.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-600 text-slate-600 mb-1">Quote Date <span className="text-red-500">*</span></label>
                <input
                  {...register('date', { required: 'Date required' })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8620A]/30 focus:border-[#E8620A]"
                  placeholder="DD/MM/YYYY"
                />
              </div>
              <div>
                <label className="block text-xs font-600 text-slate-600 mb-1">Valid Until <span className="text-red-500">*</span></label>
                <input
                  {...register('validUntil', { required: 'Validity required' })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8620A]/30 focus:border-[#E8620A]"
                  placeholder="DD/MM/YYYY"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-600 text-slate-600 mb-1">Status</label>
              <select
                {...register('status')}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8620A]/30 focus:border-[#E8620A] bg-white"
              >
                <option value="Draft">Draft</option>
                <option value="Sent">Sent</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-600 text-slate-600 mb-1">Subject / Title <span className="text-red-500">*</span></label>
              <input
                {...register('subject', { required: 'Subject required' })}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8620A]/30 focus:border-[#E8620A]"
                placeholder="e.g. Supply of LED Lighting"
              />
              {errors.subject && <p className="text-red-500 text-xs mt-1">{errors.subject.message}</p>}
            </div>

            <div>
              <label className="block text-xs font-600 text-slate-600 mb-1">Prepared By</label>
              <input
                {...register('preparedBy')}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8620A]/30 focus:border-[#E8620A]"
                placeholder="Sales person name"
              />
            </div>

            <div>
              <label className="block text-xs font-600 text-slate-600 mb-1">GST Type</label>
              <p className="text-xs text-slate-400 mb-2">CGST+SGST for same-state, IGST for inter-state transactions</p>
              <div className="flex gap-2">
                {(['CGST_SGST', 'IGST'] as GSTType[]).map(g => (
                  <button
                    key={`gst-type-${g}`}
                    type="button"
                    onClick={() => setValue('gstType', g)}
                    className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-all ${
                      watchGstType === g
                        ? 'bg-[#E8620A] text-white border-[#E8620A]'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-[#E8620A]'
                    }`}
                  >
                    {g === 'CGST_SGST' ? 'CGST + SGST' : 'IGST'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Client Info */}
          <div className="xl:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
            <h2 className="text-sm font-700 text-slate-800 flex items-center gap-2 uppercase tracking-wider">
              <Building2 size={15} className="text-[#E8620A]" />
              Client Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-600 text-slate-600 mb-1">Contact Person <span className="text-red-500">*</span></label>
                <div className="relative">
                  <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    {...register('clientName', { required: 'Client name required' })}
                    className="w-full pl-9 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8620A]/30 focus:border-[#E8620A]"
                    placeholder="Rajesh Sharma"
                  />
                </div>
                {errors.clientName && <p className="text-red-500 text-xs mt-1">{errors.clientName.message}</p>}
              </div>
              <div>
                <label className="block text-xs font-600 text-slate-600 mb-1">Company Name <span className="text-red-500">*</span></label>
                <div className="relative">
                  <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    {...register('clientCompany', { required: 'Company required' })}
                    className="w-full pl-9 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8620A]/30 focus:border-[#E8620A]"
                    placeholder="Sharma Enterprises Pvt Ltd"
                  />
                </div>
                {errors.clientCompany && <p className="text-red-500 text-xs mt-1">{errors.clientCompany.message}</p>}
              </div>
              <div>
                <label className="block text-xs font-600 text-slate-600 mb-1">Phone</label>
                <div className="relative">
                  <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    {...register('clientPhone')}
                    className="w-full pl-9 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8620A]/30 focus:border-[#E8620A]"
                    placeholder="+91 9876543210"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-600 text-slate-600 mb-1">Email</label>
                <div className="relative">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    {...register('clientEmail')}
                    type="email"
                    className="w-full pl-9 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8620A]/30 focus:border-[#E8620A]"
                    placeholder="client@company.com"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-600 text-slate-600 mb-1">GSTIN</label>
                <div className="relative">
                  <Hash size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    {...register('clientGSTIN')}
                    className="w-full pl-9 border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#E8620A]/30 focus:border-[#E8620A]"
                    placeholder="07AABCS1429B1Z2"
                    maxLength={15}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-600 text-slate-600 mb-1">Billing Address</label>
                <div className="relative">
                  <MapPin size={14} className="absolute left-3 top-3 text-slate-400" />
                  <textarea
                    {...register('clientAddress')}
                    rows={3}
                    className="w-full pl-9 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8620A]/30 focus:border-[#E8620A] resize-none"
                    placeholder="Street, City, State - PIN"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* File Upload / Import */}
        <FileUploadParser onParsed={handleFileParsed} />

        {/* Line Items */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h2 className="text-sm font-700 text-slate-800 flex items-center gap-2 uppercase tracking-wider">
              <FileText size={15} className="text-[#E8620A]" />
              Line Items
            </h2>
            <button
              type="button"
              onClick={() => append({
                id: `li-new-${Date.now()}`,
                srNo: fields.length + 1,
                description: '',
                hsn: '',
                qty: 1,
                unit: 'Nos',
                rate: 0,
                gstRate: 18,
              })}
              className="flex items-center gap-1.5 text-xs font-semibold text-[#E8620A] hover:text-[#d05508] transition-colors"
            >
              <Plus size={14} />
              Add Row
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#1B2A4A]/5 border-b border-slate-200">
                  <th className="px-3 py-2.5 text-left text-xs font-600 text-slate-500 uppercase tracking-wider w-10">#</th>
                  <th className="px-3 py-2.5 text-left text-xs font-600 text-slate-500 uppercase tracking-wider min-w-[220px]">Description</th>
                  <th className="px-3 py-2.5 text-left text-xs font-600 text-slate-500 uppercase tracking-wider w-24">HSN/SAC</th>
                  <th className="px-3 py-2.5 text-left text-xs font-600 text-slate-500 uppercase tracking-wider w-20">Qty</th>
                  <th className="px-3 py-2.5 text-left text-xs font-600 text-slate-500 uppercase tracking-wider w-24">Unit</th>
                  <th className="px-3 py-2.5 text-left text-xs font-600 text-slate-500 uppercase tracking-wider w-28">Rate (₹)</th>
                  <th className="px-3 py-2.5 text-left text-xs font-600 text-slate-500 uppercase tracking-wider w-24">GST %</th>
                  <th className="px-3 py-2.5 text-right text-xs font-600 text-slate-500 uppercase tracking-wider w-28">Amount</th>
                  <th className="px-3 py-2.5 text-right text-xs font-600 text-slate-500 uppercase tracking-wider w-28">GST Amt</th>
                  <th className="px-3 py-2.5 text-right text-xs font-600 text-slate-500 uppercase tracking-wider w-28">Total</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {fields.map((field, idx) => {
                  const computed = computedItems[idx];
                  return (
                    <tr key={field.id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-3 py-2 text-slate-400 text-xs font-medium">{idx + 1}</td>
                      <td className="px-3 py-2">
                        <input
                          {...register(`lineItems.${idx}.description`, { required: true })}
                          className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#E8620A]/40 focus:border-[#E8620A]"
                          placeholder="Item description"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          {...register(`lineItems.${idx}.hsn`)}
                          className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#E8620A]/40 focus:border-[#E8620A]"
                          placeholder="8405"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          {...register(`lineItems.${idx}.qty`, { min: 0 })}
                          type="number"
                          step="0.01"
                          className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-[#E8620A]/40 focus:border-[#E8620A]"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <select
                          {...register(`lineItems.${idx}.unit`)}
                          className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#E8620A]/40 focus:border-[#E8620A] bg-white"
                        >
                          {UNITS.map(u => <option key={`unit-${u}`} value={u}>{u}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          {...register(`lineItems.${idx}.rate`, { min: 0 })}
                          type="number"
                          step="0.01"
                          className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-[#E8620A]/40 focus:border-[#E8620A]"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <select
                          {...register(`lineItems.${idx}.gstRate`)}
                          className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#E8620A]/40 focus:border-[#E8620A] bg-white"
                        >
                          {GST_RATES.map(r => <option key={`gst-${r}`} value={r}>{r}%</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700 text-sm">
                        {formatINR(computed?.amount || 0)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700 text-sm">
                        {formatINR(computed?.gstAmount || 0)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900 text-sm">
                        {formatINR(computed?.total || 0)}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => fields.length > 1 && remove(idx)}
                          disabled={fields.length === 1}
                          className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Remove row"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totals Panel */}
          <div className="border-t border-slate-200 bg-slate-50 px-5 py-4">
            <div className="flex justify-end">
              <div className="w-full max-w-sm space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal (Taxable)</span>
                  <span className="tabular-nums font-medium text-slate-800">{formatINR(subtotal)}</span>
                </div>

                {watchGstType === 'CGST_SGST' ? (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">CGST</span>
                      <span className="tabular-nums text-slate-700">{formatINR(totalGST / 2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">SGST</span>
                      <span className="tabular-nums text-slate-700">{formatINR(totalGST / 2)}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">IGST</span>
                    <span className="tabular-nums text-slate-700">{formatINR(totalGST)}</span>
                  </div>
                )}

                <div className="flex justify-between text-sm text-slate-500">
                  <span>Total GST</span>
                  <span className="tabular-nums">{formatINR(totalGST)}</span>
                </div>

                <div className="border-t border-slate-300 pt-2 flex justify-between">
                  <span className="font-bold text-slate-900">Grand Total</span>
                  <span className="tabular-nums font-bold text-lg text-[#E8620A]">{formatINR(grandTotal)}</span>
                </div>

                <p className="text-xs text-slate-500 italic pt-1">
                  {numberToWords(grandTotal)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Terms & Notes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <label className="block text-xs font-700 text-slate-800 uppercase tracking-wider mb-2">Terms & Conditions</label>
            <textarea
              {...register('terms')}
              rows={5}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8620A]/30 focus:border-[#E8620A] resize-none"
              placeholder="Payment terms, warranty, delivery conditions..."
            />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <label className="block text-xs font-700 text-slate-800 uppercase tracking-wider mb-2">Additional Notes</label>
            <textarea
              {...register('notes')}
              rows={5}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8620A]/30 focus:border-[#E8620A] resize-none"
              placeholder="Any additional notes for the client..."
            />
          </div>
        </div>

        {/* Bottom Action Bar */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 shadow-md px-6 py-4 -mx-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            {isDirty && (
              <>
                <AlertCircle size={14} className="text-amber-500" />
                <span className="text-amber-600 font-medium">Unsaved changes</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Link href="/quotations-management" className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
              Cancel
            </Link>
            <button
              type="button"
              onClick={handleSubmit(d => onSubmit(d, 'Sent'))}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 border border-[#1B2A4A] text-[#1B2A4A] rounded-lg text-sm font-semibold hover:bg-[#1B2A4A]/5 transition-all active:scale-95 disabled:opacity-70"
            >
              Mark as Sent
            </button>
            <button
              type="button"
              onClick={handleSaveAndPreview}
              disabled={saving}
              className="flex items-center gap-2 bg-[#E8620A] hover:bg-[#d05508] text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 active:scale-95 disabled:opacity-70"
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />}
              Save & Preview
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}